import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import MetricInfoCardEntryPoint, {
	type MetricInfoCardApi,
	type MetricInfoCardEntryPointProps,
} from '@databox/ember-integrations/lib/components/MetricInfoCard/MetricInfoCardEntryPoint';
import type { ReactNode } from '@databox/ember-integrations/lib/components/Setup';

import type { ReactIntegrations, Service } from 'webapp-types';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import reactPopupRoot from 'webapp/modifiers/react-popup-root';
import willDestroy from 'webapp/modifiers/will-destroy';
import LineageExplorerHostComponent, {
	type OpenLineageExplorer,
} from 'webapp/components/lineage-explorer-host';

/**
 * The app's one metric info card (`services/metric-info`): a React root that renders nothing until an anchor opens the
 * card, which then floats over every popup (`.metric-info-manager`). It also hosts the Lineage explorer the card's
 * Lineage action opens.
 */
export default class MetricInfoManagerComponent extends Component {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-info') declare metricInfoService: Service.MetricInfo;
	@service('metric-registry') declare metricRegistryService: Service.MetricRegistry;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;

	// The Lineage explorer mounts on the card's first Lineage action, not with every page.
	@tracked isLineageMounted = false;

	reactElement: ReactIntegrations.Root = null;

	private openLineage: OpenLineageExplorer | null = null;
	// The metric the first Lineage action asked for, opened once the explorer is ready.
	private pendingLineageMetricKey: string | null = null;

	// Built once: React keeps the host as a context value and callback dependency.
	@cached
	get host(): MetricInfoCardEntryPointProps['host'] {
		return this.metricRegistryService.createMetricInfoHost(this.openLineageFor);
	}

	get entryPointProps(): MetricInfoCardEntryPointProps {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		return {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken ?? '',
			dataToken: dataToken ?? '',
			spaceId: this.accountService.id,
			user: {
				id: this.currentUserService.numericId,
				name: this.currentUserService.displayName,
			},
			host: this.host,
			isDemo: this.currentUserService.isDemo,
			// The Metrics page's gates.
			isAiEnabled: this.aiChatService.isAiChatAvailable,
			hasHome: this.currentUserService.hasHome,
			onInit: this.onReactInit,
		};
	}

	@action
	onReactInit(api: MetricInfoCardApi): void {
		this.metricInfoService.api = api;
	}

	// Mounts the explorer on the card's first Lineage action, then opens the metric once it's ready.
	@action
	openLineageFor(metricKey: string): void {
		if (this.openLineage) {
			this.openLineage({ target: { metricKey } });
			return;
		}

		this.pendingLineageMetricKey = metricKey;
		this.isLineageMounted = true;
	}

	@action
	captureLineageOpen(open: OpenLineageExplorer): void {
		this.openLineage = open;

		if (this.pendingLineageMetricKey) {
			open({ target: { metricKey: this.pendingLineageMetricKey } });
			this.pendingLineageMetricKey = null;
		}
	}

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderCard());
	}

	@action
	teardownView() {
		this.metricInfoService.api = null;
		unmountReactRoot(this);
	}

	private renderCard() {
		if (this.reactElement) {
			// @types/react@19's FunctionComponent returns ReactNode | Promise<ReactNode>;
			// reactService.render only accepts ReactNode.
			this.reactService.render(
				this.reactElement,
				MetricInfoCardEntryPoint(this.entryPointProps) as ReactNode,
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		{{! The app renders this before the account has loaded. The card needs the account's id: it tells the viewer's own
			metrics from another space's by it, and seeds the store with it. }}
		{{#if this.accountService.id}}
			<div
				class="metric-info-manager"
				{{reactPopupRoot}}
				{{didInsert this.setupView}}
				{{willDestroy this.teardownView}}
			></div>
		{{/if}}
		{{#if this.isLineageMounted}}
			<LineageExplorerHostComponent @onReady={{this.captureLineageOpen}} />
		{{/if}}
	</template>
}
